import { WarningTwoIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useResetMeetingMutation, useUpdateMeetingMutation } from '@/@generated/codegen/API';
import { useCurrentMeetingDataContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import { ROUTES } from '@/routes/Routes';
import { captureSentry } from '@/utils/helpers/sentryHelper';

type Props = {
  isAdmin: boolean;
};

export default function MeetingTestingModeWarning({ isAdmin }: Props) {
  const [currentColor, setCurrentColor] = useState<string>('red');
  const { currentMeeting: meeting } = useCurrentMeetingDataContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mutationUpdateMeeting] = useUpdateMeetingMutation();
  const [mutationResetMeeting] = useResetMeetingMutation();

  const handleDisableTestingMode = useCallback(async () => {
    if (!meeting) {
      console.warn('you are not in a meeting, you can not disable testing mode');
      captureSentry({
        title: 'MeetingTestingModeWarning handleDisableTestingMode',
      });
      return;
    }
    navigate(ROUTES.HOME);
    await mutationUpdateMeeting({
      variables: {
        data: {
          id: meeting.id,
          isInTesting: false,
        },
      },
    });
    await mutationResetMeeting({
      variables: {
        where: {
          id: meeting.id,
        },
        data: {
          setStartDate: false,
        },
      },
    });
  }, [meeting, navigate, mutationResetMeeting, mutationUpdateMeeting]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentColor((prevColor) => (prevColor === 'red' ? 'livelinx.yellow200' : 'red'));
    }, 500);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      <Box
        position="fixed"
        zIndex={10}
        top="0px"
        left="0" // Center horizontally
        right="0"
        textAlign="center"
        py="3px"
        px="5px"
        background="white"
        borderRadius="6px"
        borderColor="livelinx.orange200"
        borderStyle="dashed"
        borderWidth="2px"
        margin="5px auto"
        width="fit-content"
      >
        <Flex flexDir="row" alignItems="center">
          <WarningTwoIcon
            transition="color 0.1s ease-in-out"
            w={6}
            h={6}
            color={currentColor}
            marginRight="10px"
          />
          <Text variant="purpleTitleBold" marginRight="12px" fontSize="12px">
            You are testing the meeting
          </Text>
          {isAdmin && (
            <Button size="md" variant="primary" onClick={() => void handleDisableTestingMode()}>
              {t('common.disable')}
            </Button>
          )}
        </Flex>
      </Box>
    </>
  );
}
